import { useState, useEffect, useRef, useCallback } from 'react';
import * as Cesium from 'cesium';

/**
 * Tracks cursor latitude, longitude, altitude above terrain from the Cesium scene,
 * along with the current camera height above terrain, heading, and pitch.
 * Also tracks whether the cursor is currently over the canvas.
 */
export function useMouseCoordinates(viewer) {
  const [coordinates, setCoordinates] = useState({
    latitude: null,
    longitude: null,
    altitude: null,
    cameraHeight: null,
    heading: null,
    pitch: null,
  });
  const [isOnCanvas, setIsOnCanvas] = useState(false);

  const lastUpdateRef = useRef(0);
  const rafIdRef = useRef(null);

  const getHeightAboveTerrain = useCallback((cartographic) => {
    if (!viewer || viewer.isDestroyed()) return cartographic.height;
    try {
      const terrainHeight = viewer.scene.globe.getHeight(cartographic);
      if (terrainHeight !== undefined && terrainHeight !== null) {
        return cartographic.height - terrainHeight;
      }
    } catch { /* ignore */ }
    return cartographic.height;
  }, [viewer]);

  const updateCameraInfo = useCallback(() => {
    if (!viewer || viewer.isDestroyed()) return;
    try {
      const camera = viewer.camera;
      const camCarto = camera.positionCartographic;
      const cameraHeightAboveTerrain = getHeightAboveTerrain(camCarto);
      setCoordinates((prev) => ({
        ...prev,
        cameraHeight: cameraHeightAboveTerrain,
        heading: Cesium.Math.toDegrees(camera.heading),
        pitch: Cesium.Math.toDegrees(camera.pitch),
      }));
    } catch { /* ignore */ }
  }, [viewer, getHeightAboveTerrain]);

  useEffect(() => {
    if (!viewer || viewer.isDestroyed()) return;

    const canvas = viewer.scene.canvas;

    const handleMouseMove = (event) => {
      const now = performance.now();
      if (now - lastUpdateRef.current < 100) return;

      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        lastUpdateRef.current = performance.now();

        try {
          const rect = canvas.getBoundingClientRect();
          const screenPosition = new Cesium.Cartesian2(
            event.clientX - rect.left,
            event.clientY - rect.top
          );

          const cartesian = viewer.scene.pickPosition(screenPosition);
          const camera = viewer.camera;
          const camCarto = camera.positionCartographic;
          const cameraHeightAboveTerrain = getHeightAboveTerrain(camCarto);
          const headingDeg = Cesium.Math.toDegrees(camera.heading);
          const pitchDeg = Cesium.Math.toDegrees(camera.pitch);

          if (cartesian && Cesium.defined(cartesian)) {
            const cartographic = Cesium.Cartographic.fromCartesian(cartesian);
            const altAboveTerrain = getHeightAboveTerrain(cartographic);
            setCoordinates({
              latitude: Cesium.Math.toDegrees(cartographic.latitude),
              longitude: Cesium.Math.toDegrees(cartographic.longitude),
              altitude: altAboveTerrain,
              cameraHeight: cameraHeightAboveTerrain,
              heading: headingDeg,
              pitch: pitchDeg,
            });
          } else {
            setCoordinates((prev) => ({
              ...prev,
              cameraHeight: cameraHeightAboveTerrain,
              heading: headingDeg,
              pitch: pitchDeg,
            }));
          }
        } catch {
          // Scene may not be ready or pick failed — ignore
        }
      });
    };

    const handleMouseEnter = () => setIsOnCanvas(true);
    const handleMouseLeave = () => setIsOnCanvas(false);

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseenter', handleMouseEnter);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    // Update camera info on render (so heading/pitch/height update during keyboard/wheel nav)
    const removePostRender = viewer.scene.postRender.addEventListener(updateCameraInfo);

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseenter', handleMouseEnter);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
      removePostRender();
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, [viewer, updateCameraInfo, getHeightAboveTerrain]);

  return { ...coordinates, isOnCanvas };
}
